import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
/**
 * Duplicate rules (RELAY-947 part 2). Per organisation, each matching field has a mode:
 *   block — creating (or editing into) a candidate whose email/phone another live candidate
 *           already has is refused;
 *   warn  — it is refused once with the matches, and goes through when resent with
 *           `allowDuplicate=true` (the person saw the warning and chose to continue);
 *   off   — no check.
 * Matching is on the normalised value: email lower-cased and trimmed, phone digits only — the
 * same expressions as the generated `primary_email` / `primary_phone` columns.
 */
export const DEDUPE_FIELDS = ['email', 'phone'];
export const dedupeFieldSchema = z.enum(DEDUPE_FIELDS);
export const DEDUPE_MODES = ['off', 'warn', 'block'];
export const dedupeModeSchema = z.enum(DEDUPE_MODES);
/**
 * Until an admin changes them, both fields BLOCK — exactly what the unique indexes enforced
 * before (RELAY-939), so introducing rules changes nothing on its own.
 */
export const DEFAULT_DEDUPE_RULES = {
    email: 'block',
    phone: 'block',
};
export const dedupeRuleSchema = z.object({
    entity: z.literal('candidates'),
    field: dedupeFieldSchema,
    mode: dedupeModeSchema,
    /** False while the organisation is on the default for this field. */
    isCustomised: z.boolean(),
});
export const dedupeRuleInputSchema = z.object({
    entity: z.literal('candidates').default('candidates'),
    field: dedupeFieldSchema,
    mode: dedupeModeSchema,
});
export const normaliseEmail = (v) => {
    const e = (v ?? '').trim().toLowerCase();
    return e || null;
};
export const normalisePhone = (v) => {
    const p = (v ?? '').replace(/[^0-9]/g, '');
    return p || null;
};
/** One existing candidate a new or edited one collides with. Names only for visible ones. */
export const duplicateMatchSchema = z.object({
    field: dedupeFieldSchema,
    candidateId: idSchema.nullable(),
    name: z.string().nullable(),
});
/** The nightly report: groups of live candidates sharing a normalised email or phone. */
export const duplicateGroupSchema = z.object({
    field: dedupeFieldSchema,
    value: z.string(),
    candidates: z.array(z.object({
        id: idSchema,
        name: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        stage: z.string().nullable(),
        createdAt: instantSchema,
    })),
});
export const duplicateReportSchema = z.object({
    generatedAt: instantSchema.nullable(),
    groups: z.array(duplicateGroupSchema),
});
/** Fields a merge can take from either side, standard ones by name and custom as `custom.<key>`. */
export const MERGEABLE_FIELDS = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'altPhone',
    'dateOfBirth',
    'gender',
    'currentRole',
    'currentCompany',
    'yearsExperience',
    'educationLevel',
    'workType',
    'region',
    'country',
    'languages',
    'expectedHourlyRate',
    // Not `stage`: it mirrors the candidate's place in the default Candidate Flow pipeline, and a
    // merge moves the other's places to the survivor (the survivor's own place wins).
    'status',
    'source',
    'ownerId',
    'applicationDate',
    'interviewDate',
    'onboardingDate',
    'resumeDocumentId',
    'portfolioUrl',
    'recruitCrmId',
    'recruitCrmSlug',
    'relayResumeId',
];
export const mergePreviewSchema = z.object({
    survivorId: idSchema,
    otherId: idSchema,
    fields: z.array(z.object({
        field: z.string(),
        survivor: z.unknown(),
        other: z.unknown(),
        /** The survivor's value unless it is empty and the other's is not. */
        suggested: z.enum(['survivor', 'other']),
        /** Same on both sides: nothing to choose. */
        same: z.boolean(),
    })),
    /** What moves to the survivor whatever is chosen. */
    moves: z.object({
        notes: z.number().int(),
        tasks: z.number().int(),
        documents: z.number().int(),
    }),
});
export const mergeRequestSchema = z.object({
    otherId: idSchema,
    /** Fields to take from the other candidate; every other field keeps the survivor's value. */
    takeFromOther: z.array(z.string().min(1).max(100)).max(200).default([]),
});
//# sourceMappingURL=duplicates.js.map